import { forwardRef, useImperativeHandle, useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

/**
 * A message dialog component with an imperative API for displaying informational messages.
 * Uses forwardRef to expose show() and hide() methods to parent components.
 * Wraps Radix UI's AlertDialog with pre-configured single-action behavior.
 *
 * @param {Object} props - Component props
 * @param {string} [props.variant='default'] - Visual variant of the dialog ('default' or 'destructive')
 * @param {React.Ref} ref - Forwarded ref exposing imperative methods
 *
 * @typedef {Object} ShowConfig
 * @property {string} title - The title text displayed in the dialog header
 * @property {string} message - The message text explaining the information or result
 * @property {string} [okText='OK'] - Text for the acknowledgment button
 * @property {Function} [onClose] - Optional callback function executed when dialog closes
 *
 * @typedef {Object} MessageDialogRef
 * @property {function(ShowConfig): void} show - Display the dialog with the given configuration
 * @property {function(): void} hide - Programmatically hide the dialog
 *
 * @example
 * // Basic usage with success message
 * import { useRef } from 'react'
 * import { MessageDialog } from '@/components/common/MessageDialog'
 *
 * function MyComponent() {
 *   const messageRef = useRef(null)
 *
 *   const handleSave = async () => {
 *     await saveData()
 *     messageRef.current.show({
 *       title: 'Success',
 *       message: 'Your changes have been saved successfully.',
 *       okText: 'Got it'
 *     })
 *   }
 *
 *   return (
 *     <>
 *       <button onClick={handleSave}>Save</button>
 *       <MessageDialog ref={messageRef} />
 *     </>
 *   )
 * }
 *
 * @example
 * // Destructive variant with callback
 * import { useRef } from 'react'
 * import { MessageDialog } from '@/components/common/MessageDialog'
 *
 * function ErrorHandler() {
 *   const errorDialogRef = useRef(null)
 *
 *   const showError = (errorMessage) => {
 *     errorDialogRef.current.show({
 *       title: 'Error',
 *       message: errorMessage,
 *       okText: 'Dismiss',
 *       onClose: () => console.log('Error dialog dismissed')
 *     })
 *   }
 *
 *   return <MessageDialog ref={errorDialogRef} variant="destructive" />
 * }
 *
 * @example
 * // Default variant for informational message
 * const infoRef = useRef(null)
 *
 * infoRef.current.show({
 *   title: 'Information',
 *   message: 'This feature is currently in beta.'
 * })
 *
 * // TODO: Does not queue multiple dialogs - calling show() while dialog is open will replace the current message
 */
export const MessageDialog = forwardRef(({ variant = 'default' }, ref) => {
  const [open, setOpen] = useState(false)
  const [config, setConfig] = useState({
    title: '',
    message: '',
    okText: 'OK',
    onClose: null
  })

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    show: ({ title, message, okText = 'OK', onClose }) => {
      setConfig({ title, message, okText, onClose })
      setOpen(true)
    },
    hide: () => {
      setOpen(false)
    }
  }))

  const handleClose = () => {
    setOpen(false)
    config.onClose?.()
    // Clear config after animation completes
    setTimeout(() => setConfig({
      title: '',
      message: '',
      okText: 'OK',
      onClose: null
    }), 200)
  }

  const isDestructive = variant === 'destructive'

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{config.title}</AlertDialogTitle>
          <AlertDialogDescription>
            {config.message}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={handleClose}
            style={isDestructive ? {
              backgroundColor: 'hsl(var(--color-destructive))',
              color: 'hsl(var(--color-destructive-foreground))',
              borderColor: 'hsl(var(--color-destructive))'
            } : undefined}
          >
            {config.okText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
})

MessageDialog.displayName = "MessageDialog"
